import { useCallback, useEffect, useState } from 'react';

export default function useFocusedElement<Element extends HTMLElement>(
  ref: React.RefObject<Element>
): boolean {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(
    e => {
      const currentRef = ref.current;

      if (e.type === 'focusin' && e.target === currentRef) {
        setIsFocused(true);
      }
    },
    [ref]
  );

  const handleBlur = useCallback(
    e => {
      const currentRef = ref.current;

      if (e.type === 'focusout' && e.target === currentRef) {
        setIsFocused(false);
      }
    },
    [ref]
  );

  useEffect(() => {
    // Note: `focusin` is sent whenever an element OR its children gain focus (or, it supports "event bubbling"). If you need to isolate it to only the parent, create a new function using 'focus' and 'blur' respectively.

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, [handleFocus, handleBlur]);

  return Boolean(isFocused);
}
